<template>
  <Teleport to="body">
    <div v-if="show" class="consultant-pay-bill-details-modal-overlay">
      <div
        class="consultant-pay-bill-details-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vendor-direct-expense-modal-title"
        @click.stop
      >
        <header class="consultant-pay-bill-details-modal-header">
          <div class="consultant-pay-bill-details-modal-heading">
            <h2 id="vendor-direct-expense-modal-title" class="consultant-pay-bill-details-modal-sr-only">
              {{ accessibilityTitle }}
            </h2>
            <div class="consultant-pay-bill-details-modal-row-primary-wrap">
              <div class="consultant-pay-bill-details-modal-row-primary">
                <span v-if="displayVendor" class="consultant-pay-bill-details-modal-customer-name">{{ displayVendor }}</span>
                <template v-if="displayVendor && expenseTitle">
                  <span class="consultant-pay-bill-details-modal-primary-sep" aria-hidden="true">—</span>
                </template>
                <span v-if="expenseTitle" class="consultant-pay-bill-details-modal-invoice-no">
                  {{ expenseKindLabel }}: <strong>{{ expenseTitle }}</strong>
                </span>
              </div>
            </div>
            <div v-if="headerMetaChips.length || expenseMemo" class="consultant-pay-bill-details-modal-row-details-wrap">
              <div class="consultant-pay-bill-details-modal-row-details">
                <div
                  v-for="(chip, idx) in headerMetaChips"
                  :key="'expense-chip-' + idx"
                  class="consultant-pay-bill-details-modal-detail-chip"
                >
                  <span class="consultant-pay-bill-details-modal-detail-k">{{ chip.label }}</span>
                  <span class="consultant-pay-bill-details-modal-detail-v">{{ chip.value }}</span>
                </div>
                <button
                  v-if="expenseMemo"
                  type="button"
                  class="consultant-pay-bill-details-modal-memo-btn"
                  :aria-expanded="memoOpen ? 'true' : 'false'"
                  @click="memoOpen = !memoOpen"
                >
                  Memo
                </button>
              </div>
            </div>
          </div>
          <button
            type="button"
            class="consultant-pay-bill-details-modal-close"
            aria-label="Close"
            title="Close"
            @click="handleClose"
          >
            <i class="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </header>

        <div class="consultant-pay-bill-details-modal-body">
          <div v-if="loading" class="consultant-pay-bill-details-modal-empty consultant-pay-bill-details-modal-empty--compact">
            Loading expense details...
          </div>
          <div v-else-if="error" class="consultant-pay-bill-details-modal-empty consultant-pay-bill-details-modal-empty--compact">
            {{ error }}
          </div>
          <template v-else-if="expense">
            <section
              v-show="memoOpen && expenseMemo"
              class="consultant-pay-bill-details-modal-summary consultant-pay-bill-details-modal-summary--notes-only"
            >
              <div class="consultant-pay-bill-details-modal-extra">
                <div class="consultant-pay-bill-details-modal-extra-line consultant-pay-bill-details-modal-extra-line--memo">
                  <span class="consultant-pay-bill-details-modal-extra-k">Memo</span>
                  <span class="consultant-pay-bill-details-modal-extra-v">{{ expenseMemo }}</span>
                </div>
              </div>
            </section>

            <nav
              v-if="hasTabbedContent"
              class="consultant-pay-bill-details-modal-tabs"
              role="tablist"
              aria-label="Expense details"
            >
              <button
                type="button"
                role="tab"
                class="consultant-pay-bill-details-modal-tab"
                :class="{ 'consultant-pay-bill-details-modal-tab--active': bodyView === 'lines' }"
                :aria-selected="bodyView === 'lines' ? 'true' : 'false'"
                @click="switchBodyTab('lines')"
              >
                {{ linesTabLabel }}
              </button>
              <button
                v-if="hasJournalEntry"
                type="button"
                role="tab"
                class="consultant-pay-bill-details-modal-tab"
                :class="{ 'consultant-pay-bill-details-modal-tab--active': bodyView === 'journal' }"
                :aria-selected="bodyView === 'journal' ? 'true' : 'false'"
                @click="switchBodyTab('journal')"
              >
                Journal entry
              </button>
            </nav>

            <div
              v-if="hasTabbedContent"
              class="consultant-pay-bill-details-modal-tab-panel"
              role="tabpanel"
            >
              <template v-if="bodyView === 'lines'">
                <div
                  v-if="!hasAnyLines"
                  class="consultant-pay-bill-details-modal-empty consultant-pay-bill-details-modal-empty--compact"
                >
                  No line details found for this expense.
                </div>
                <div v-else class="consultant-pay-bill-details-modal-table-scroll">
                  <table
                    v-if="accountRecords.length"
                    class="consultant-pay-bill-details-modal-table consultant-pay-bill-details-modal-table--receipt"
                  >
                    <thead>
                      <tr>
                        <th scope="col" class="consultant-pay-bill-details-modal-th">Account</th>
                        <th scope="col" class="consultant-pay-bill-details-modal-th">Description</th>
                        <th scope="col" class="consultant-pay-bill-details-modal-th consultant-pay-bill-details-modal-th--num">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, idx) in accountRecords"
                        :key="'account-' + idx"
                        class="consultant-pay-bill-details-modal-tr"
                      >
                        <td class="consultant-pay-bill-details-modal-td">{{ row.accountName || formatAccount(row.accountId) }}</td>
                        <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--desc">
                          {{ row.displayDescription || row.description || '-' }}
                        </td>
                        <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--num">
                          {{ formatCurrency(row.amount) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <table
                    v-if="itemRecords.length"
                    class="consultant-pay-bill-details-modal-table consultant-pay-bill-details-modal-table--receipt"
                    :class="{ 'consultant-pay-bill-details-modal-table--spaced': accountRecords.length }"
                  >
                    <thead>
                      <tr>
                        <th scope="col" class="consultant-pay-bill-details-modal-th">Item</th>
                        <th scope="col" class="consultant-pay-bill-details-modal-th">Description</th>
                        <th scope="col" class="consultant-pay-bill-details-modal-th consultant-pay-bill-details-modal-th--num">Qty</th>
                        <th scope="col" class="consultant-pay-bill-details-modal-th consultant-pay-bill-details-modal-th--num">Unit price</th>
                        <th scope="col" class="consultant-pay-bill-details-modal-th consultant-pay-bill-details-modal-th--num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, idx) in itemRecords"
                        :key="'item-' + idx"
                        class="consultant-pay-bill-details-modal-tr"
                      >
                        <td class="consultant-pay-bill-details-modal-td">{{ row.itemName || '-' }}</td>
                        <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--desc">
                          {{ row.displayDescription || row.description || row.itemName || '-' }}
                        </td>
                        <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--num">
                          {{ row.quantity || 0 }}
                        </td>
                        <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--num">
                          {{ formatCurrency(row.unitPrice) }}
                        </td>
                        <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--num">
                          {{ formatCurrency(row.total || lineTotal(row)) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>

              <div v-else class="consultant-pay-bill-details-modal-table-scroll">
                <div
                  v-if="journalLoading"
                  class="consultant-pay-bill-details-modal-empty consultant-pay-bill-details-modal-empty--compact"
                >
                  Loading journal entry...
                </div>
                <div
                  v-else-if="journalError"
                  class="consultant-pay-bill-details-modal-empty consultant-pay-bill-details-modal-empty--compact"
                >
                  <p class="consultant-pay-bill-details-modal-je-error">{{ journalError }}</p>
                  <button
                    type="button"
                    class="consultant-pay-bill-details-modal-je-retry"
                    @click="loadJournalEntryDetail"
                  >
                    Retry
                  </button>
                </div>
                <div
                  v-else-if="!flattenedJournalRows.length"
                  class="consultant-pay-bill-details-modal-empty consultant-pay-bill-details-modal-empty--compact"
                >
                  No journal entry linked to this expense.
                </div>
                <table
                  v-else
                  class="consultant-pay-bill-details-modal-table consultant-pay-bill-details-modal-table--journal"
                >
                  <thead>
                    <tr>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Entry #</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Date</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Status</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Account</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Memo</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th consultant-pay-bill-details-modal-th--num">Debit</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th consultant-pay-bill-details-modal-th--num">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in flattenedJournalRows"
                      :key="row.rowKey"
                      class="consultant-pay-bill-details-modal-tr"
                    >
                      <td class="consultant-pay-bill-details-modal-td">{{ row.entryNo || '—' }}</td>
                      <td class="consultant-pay-bill-details-modal-td">{{ formatDate(row.entryDate) }}</td>
                      <td class="consultant-pay-bill-details-modal-td">{{ formatStatus(row.status) }}</td>
                      <td class="consultant-pay-bill-details-modal-td">{{ row.accountName }}</td>
                      <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--desc">{{ row.memo }}</td>
                      <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--num consultant-pay-bill-details-modal-td--je-debit">
                        {{ formatCurrency(row.debit) }}
                      </td>
                      <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--num consultant-pay-bill-details-modal-td--je-credit">
                        {{ formatCurrency(row.credit) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import '@/components/PayBillDetailsModal.css';
import ApiService from '@/services/api.service.js';
import JournalEntryService from '@/services/journalEntry.service.js';
import logger from '@/utils/logger';

export default {
  name: 'VendorDirectExpenseDetailsModal',
  props: {
    show: { type: Boolean, default: false },
    expenseId: { type: [String, Number], default: null },
    expenseKind: { type: String, default: '' },
    expenseNo: { type: String, default: '' },
    vendorName: { type: String, default: '' },
    companyId: { type: [String, Number], default: null },
    accountsMap: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close'],
  data() {
    return {
      expense: null,
      loading: false,
      error: '',
      bodyView: 'lines',
      memoOpen: false,
      journalEntryDetail: null,
      journalLoading: false,
      journalError: '',
      journalLoadedId: null,
    };
  },
  computed: {
    displayVendor() {
      return (this.vendorName || this.expense?.vendorName || '').trim();
    },
    expenseKindLabel() {
      return this.expenseKind === 'Cash Expense' ? 'Cash expense' : 'Credit card expense';
    },
    expenseTitle() {
      if (this.expense?.displayNumber) return this.expense.displayNumber;
      if (this.expense?.journalEntry?.entryNo) return this.expense.journalEntry.entryNo;
      const prefix = this.expenseKind === 'Cash Expense' ? 'CE' : 'CCE';
      if (this.expenseId) {
        return `${prefix}-${String(this.expenseId).padStart(4, '0')}`;
      }
      return (this.expenseNo || this.expense?.number || '').trim();
    },
    accessibilityTitle() {
      return `${this.expenseKindLabel} ${this.expenseTitle}`.trim();
    },
    accountRecords() {
      return Array.isArray(this.expense?.accountRecords) ? this.expense.accountRecords : [];
    },
    itemRecords() {
      return Array.isArray(this.expense?.itemRecords) ? this.expense.itemRecords : [];
    },
    hasAnyLines() {
      return this.accountRecords.length > 0 || this.itemRecords.length > 0;
    },
    hasJournalEntry() {
      return Boolean(this.expense?.journalEntry?.id);
    },
    hasTabbedContent() {
      return this.hasAnyLines || this.hasJournalEntry;
    },
    linesTabLabel() {
      if (this.accountRecords.length && !this.itemRecords.length) return 'Account lines';
      if (this.itemRecords.length && !this.accountRecords.length) return 'Product / service lines';
      return 'Expense lines';
    },
    flattenedJournalRows() {
      const entry = this.journalEntryDetail;
      if (!entry) return [];
      const rows = [];
      const base = {
        entryNo: entry.entryNo,
        entryDate: entry.entryDate,
        status: entry.status,
      };
      const entryLines = Array.isArray(entry.entryLines) ? entry.entryLines : [];
      if (entryLines.length === 0) {
        rows.push({
          ...base,
          accountName: '—',
          memo: entry.comments ? String(entry.comments) : '—',
          debit: null,
          credit: null,
          rowKey: `je-${entry.id}-empty`,
        });
      } else {
        for (const line of entryLines) {
          const lid = line.id != null ? line.id : `${line.accountId}-${rows.length}`;
          rows.push({
            ...base,
            accountName: line.account?.accountName || '—',
            memo: (line.memo && String(line.memo).trim()) ? line.memo : '—',
            debit: line.debit,
            credit: line.credit,
            rowKey: `je-${entry.id}-ln-${lid}`,
          });
        }
      }
      return rows;
    },
    headerMetaChips() {
      if (!this.expense) return [];
      const chips = [];
      if (this.expense.date) {
        chips.push({ label: 'Date', value: this.formatDate(this.expense.date) });
      }
      chips.push({ label: 'Status', value: 'Paid' });
      if (this.expense.total != null) {
        chips.push({ label: 'Total', value: this.formatCurrency(this.expense.total) });
      }
      const payFrom = this.formatPayFrom();
      if (payFrom !== '-') {
        chips.push({ label: 'Pay from', value: payFrom });
      }
      return chips;
    },
    expenseMemo() {
      const text = this.expense?.comments;
      return text && String(text).trim() ? String(text).trim() : '';
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(visible) {
        if (visible) {
          this.bodyView = 'lines';
          this.memoOpen = false;
          this.resetJournalState();
          this.loadExpense();
        } else {
          this.expense = null;
          this.error = '';
          this.bodyView = 'lines';
          this.memoOpen = false;
          this.resetJournalState();
        }
      },
    },
    expenseId() {
      if (this.show) {
        this.bodyView = 'lines';
        this.resetJournalState();
        this.loadExpense();
      }
    },
  },
  methods: {
    handleClose() {
      this.$emit('close');
    },
    resetJournalState() {
      this.journalEntryDetail = null;
      this.journalLoading = false;
      this.journalError = '';
      this.journalLoadedId = null;
    },
    async switchBodyTab(view) {
      this.bodyView = view;
      if (view === 'journal') {
        await this.loadJournalEntryDetail();
      }
    },
    async loadJournalEntryDetail() {
      const journalId = this.expense?.journalEntry?.id;
      if (!journalId) return;
      if (this.journalLoadedId === journalId && !this.journalError) return;

      this.journalLoading = true;
      this.journalError = '';
      try {
        const entry = await JournalEntryService.getJournalEntryById(journalId);
        this.journalEntryDetail = entry || null;
        this.journalLoadedId = journalId;
      } catch (err) {
        logger.error('VendorDirectExpenseDetailsModal: failed to load journal entry', err);
        this.journalError =
          err.response?.data?.message
          || err.response?.data?.error
          || err.message
          || 'Could not load journal entry.';
        this.journalEntryDetail = null;
      } finally {
        this.journalLoading = false;
      }
    },
    async loadExpense() {
      if (!this.expenseId) {
        this.error = 'Expense not found.';
        return;
      }
      this.loading = true;
      this.error = '';
      try {
        const endpoint = this.expenseKind === 'Cash Expense'
          ? `/vendor/cash-expense/${this.expenseId}`
          : `/vendor/credit-card-expense/${this.expenseId}`;
        const response = await ApiService.get(endpoint);
        this.expense = response.data || null;
        if (!this.expense) {
          this.error = 'Expense not found.';
        }
      } catch (err) {
        logger.error('VendorDirectExpenseDetailsModal: failed to load expense', err);
        this.error = err.response?.data?.error || err.message || 'Failed to load expense details.';
        this.expense = null;
      } finally {
        this.loading = false;
      }
    },
    lineTotal(row) {
      return (Number(row.quantity) || 0) * (Number(row.unitPrice) || 0);
    },
    formatPayFrom() {
      const account = this.expense?.payFromAccount;
      if (account) {
        const num = account.accountNumber || '';
        const name = account.accountName || '';
        return num ? `${num} - ${name}` : name || '-';
      }
      return this.formatAccount(this.expense?.payFrom);
    },
    formatAccount(accountId) {
      if (!accountId) return '-';
      const account = this.accountsMap?.[accountId];
      if (!account) return String(accountId);
      const num = account.accountNumber || '';
      const name = account.accountName || '';
      return num ? `${num} - ${name}` : name || String(accountId);
    },
    formatCurrency(amount) {
      if (amount === undefined || amount === null || amount === '') return '—';
      const value = Number(amount);
      if (!Number.isFinite(value)) return '—';
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    },
    formatStatus(status) {
      if (!status) return '—';
      return String(status)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-US');
    },
  },
};
</script>
